import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import './Footer.css';

// Blog URL from environment (defaults to production)
const BLOG_URL = import.meta.env.VITE_BLOG_URL || 'https://blog.raisa.chat';

interface FooterLink {
    label: string;
    to: string;
    external?: boolean;
}

export default function StandardFooter() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const locale = i18n.language.split('-')[0] === 'ro' ? 'ro' : 'en';

    const getLocalizedPath = (path: string) => {
        if (path.startsWith('#')) return path;
        const lang = i18n.language.split('-')[0];
        if (lang === 'en') return path;
        return `/${lang}${path === '/' ? '' : path}`;
    };

    const handleNavigation = (link: FooterLink) => {
        if (link.external) {
            window.open(link.to, '_blank', 'noopener,noreferrer');
            return;
        }
        if (link.to.startsWith('#')) {
            window.location.hash = link.to.substring(1);
            return;
        }
        navigate({ to: getLocalizedPath(link.to) as any });
    };

    const sections: { title: string; links: FooterLink[] }[] = [
        {
            title: t('common.company'),
            links: [
                { label: t('common.home'), to: '/' },
                { label: t('common.about'), to: '/about' },
                { label: t('common.contact'), to: '/contact' },
                { label: t('common.faq'), to: '/#faq-section' },
            ]
        },
        {
            title: t('common.product'),
            links: [
                { label: t('home.ai_planner'), to: '/' },
                { label: t('common.blog'), to: `${BLOG_URL}/${locale}`, external: true },
                { label: t('common.city_guides', 'City Guides'), to: `${BLOG_URL}/${locale}/city-guides`, external: true },
            ]
        },
        {
            title: t('common.legal'),
            links: [
                { label: t('common.privacy'), to: '/privacy' },
                { label: t('common.terms'), to: '/terms' },
                { label: t('common.cookies'), to: '/cookies' },
                { label: t('common.imprint'), to: '/imprint' },
            ]
        },
        {
            title: t('common.top_destinations', 'Top Destinations'),
            links: [
                { label: 'Roma', to: `${BLOG_URL}/${locale}/city-guides/roma`, external: true },
                { label: 'Budapest', to: `${BLOG_URL}/${locale}/city-guides/budapest`, external: true },
                { label: t('common.all_destinations', 'All Destinations'), to: `${BLOG_URL}/${locale}/city-guides`, external: true },
            ]
        }
    ];

    return (
        <footer className="footer-premium">
            <div className="footer-grid">
                {sections.map(section => (
                    <div key={section.title} className="footer-col">
                        <h4>{section.title}</h4>
                        <ul>
                            {section.links.map(link => (
                                <li key={link.label}>
                                    <button
                                        onClick={() => handleNavigation(link)}
                                        className="footer-link-btn"
                                    >
                                        {link.label}
                                        {link.external && <span className="external-icon"> ↗</span>}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            <div className="footer-bottom">
                <div className="copyright">
                    © 2026 RAISIS GROUP SRL • {t('common.made_with')} 🧡 {t('common.in_romania')}
                </div>
                <div className="social-links">
                    <a href="#" aria-label="TikTok">TikTok</a>
                    <a href="#" aria-label="Instagram">Instagram</a>
                    <a href="#" aria-label="LinkedIn">LinkedIn</a>
                    <a href="#" aria-label="YouTube">YouTube</a>
                </div>
            </div>

            <div className="footer-powered-by">
                <a href="https://raisissoftware.com" target="_blank" rel="noopener noreferrer">
                    <img src="/poweredByRaisisSoftware.png" alt="Powered by Raisis Software" />
                </a>
            </div>
        </footer>
    );
}
